/**
 * Telling the server "still here" while the app is open.
 *
 * Presence expires on `presenceTtl` and nothing in the foreground restarted
 * that clock (reads are pure; last_seen writers are all one-shot user
 * actions) — so a user reading Nearby at a table went stale while their own
 * screen said Visible. stay-visible.ts cannot carry this alone: it needs
 * "Always" permission and on iOS is movement-driven (`timeInterval` is
 * Android-only), making the person sitting still the least likely to be
 * reported — backwards. This covers the foreground without asking the OS for
 * anything.
 *
 * No RUNTIME imports: dependencies are injected so plain node tests can
 * drive it (session-store.ts pulls react-native and cannot be). The
 * constants below deliberately restate numbers from api/retry-after.ts:
 * transport and scheduling own their own pacing and are free to diverge.
 */

/**
 * How often a foregrounded app says "still here". Two minutes against a
 * presenceTtl of five IS the design: one failed beat is absorbed (next lands
 * at 240 s, inside the 300 s window); only two consecutive failures let
 * someone lapse — and by then lapsing is honest. Not the original 45 s: the
 * worst case is set by the TTL, not the cadence (faster buys a better
 * average, no better guarantee, at 2.7x the GPS reads), and a locked phone
 * already coasts on the full five minutes. Cost, stated: typical staleness
 * ~120 s ≈ 170 m at walking pace — irrelevant at the 5 km default, and at
 * the 500 m slider end the accept-time range check refuses kindly. If narrow-
 * radius accuracy ever matters, scale the cadence with the user's radius.
 */
export const PRESENCE_HEARTBEAT_MS = 120_000;

/**
 * How stale the OS's cached fix may be before a beat pays for a live read —
 * the battery lever (a cached read is free; a live one spins up the location
 * stack on a 4–12 s budget). DERIVED, not chosen: the oldest a stored
 * coordinate can be is this + PRESENCE_HEARTBEAT_MS = 240 s, which must stay
 * inside presenceTtl (300 s). One interval also means live reads land every
 * OTHER beat — half the location work, same liveness — and the cache cannot
 * rot: past this age the next beat falls through to a live read. Heartbeat
 * only; anything a person asked for takes the live read.
 */
export const HEARTBEAT_CACHED_FIX_MAX_AGE_MS = PRESENCE_HEARTBEAT_MS;

/** Why a beat did or did not reach the server. Returned so tests can see it. */
export type BeatOutcome =
  | "sent"
  | "failed"
  | "skipped_no_fix"
  | "skipped_background"
  | "skipped_overlapping"
  | "skipped_just_published"
  | "skipped_backpressure"
  | "stopped";

type Deps = {
  /**
   * Refresh presence server-side. Resolves `true` when presence was actually
   * refreshed and `false` when it could not be — see `makePresencePing`, which
   * is the only implementation the app uses.
   */
  ping: () => Promise<boolean>;
  /**
   * Whether the app is actually in use. Checked on every beat rather than once
   * at start, because the answer changes without the heartbeat being told.
   *
   * It matters most on **web**, where a hidden tab keeps its timers running: a
   * laptop left open on a desk would otherwise report its owner present all
   * day, from a position they have long since walked away from. On native the
   * OS suspends JS timers when backgrounded, so this is belt and braces there —
   * and staying visible while backgrounded is `stay-visible.ts`'s job, gated on
   * a permission the user granted for exactly that.
   */
  isForeground: () => boolean;
};

/**
 * The beat itself: refresh WHERE the user is, then that they are still here —
 * in that order because different endpoints write them. join sets
 * last_seen/visible/status and no coordinates; only checkIn (GET
 * /venues/current) calls applyCoordinates. join alone would refresh LIVENESS
 * while pinning POSITION — indefinitely cueable at the wrong place, strictly
 * worse than lapsing (invisible fails safe). This invariant — every writer
 * extending last_seen also refreshes the position — is what makes the 300 s
 * TTL defensible and canCueUser's distance check meaningful; stay-visible.ts
 * keeps it for the background with the same two calls in the same order.
 *
 * NO FIX MEANS NO PING — the user lapses, which is honest; not a
 * coordinate-less join (the fail-open case in another hat). Same choice
 * stay-visible.ts makes on missing accuracy.
 *
 * KNOWN RESIDUAL: getFix falls back to a cached position up to five minutes
 * old when the live read times out (indoors — exactly a cafe), which is then
 * stamped fresh and eligible for five more — a worst case of ~ten minutes,
 * immaterial at 5 km, NOT at the 500 m slider end. Not fixable here: the
 * durable fix is server-side eligibility reading coordinates_updated_at
 * instead of last_seen alone (presence.entity.ts measured the index cost).
 */
export function makePresencePing<TCoords>({
  getFix,
  checkIn,
  join,
  stillWantsVisible,
}: {
  /** Resolve the current position, or `null` when there isn't a usable one. */
  getFix: () => Promise<TCoords | null>;
  /** `GET /venues/current` — saves the fix, resolves the space, checks in. */
  checkIn: (coords: TCoords) => Promise<unknown>;
  /** `POST /presence/join` — keeps the user visible where they just checked in. */
  join: () => Promise<unknown>;
  /**
   * The user's CURRENT visibility intent, read at the moment of asking. A
   * beat is not an instant (getFix can wait 12 s) and stop() only prevents
   * FUTURE beats — a user tapping Hidden mid-beat used to be silently
   * republished by the stale beat's join(): a privacy failure, not
   * staleness. Checked twice — on entry (no work when unwanted) and
   * immediately before join(), after the slow legs, shrinking the window to
   * one in-flight HTTP request (irreducible client-side; the hide retry loop
   * is the authority). checkIn is deliberately NOT re-gated: it writes
   * coordinates, never `visible` — the JOIN is what would republish them.
   */
  stillWantsVisible: () => boolean;
}): () => Promise<boolean> {
  return async () => {
    if (!stillWantsVisible()) return false;
    const coords = await getFix();
    if (coords === null) return false;
    await checkIn(coords);
    if (!stillWantsVisible()) return false;
    await join();
    return true;
  };
}

let deps: Deps | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
/**
 * One beat at a time. A request slower than the interval would otherwise stack
 * up behind itself, and a phone on a bad connection is precisely where that
 * happens — turning one stalled ping into an unbounded queue of them.
 */
let inFlight = false;

/**
 * When a server refusal says we may beat again. 0 means "no refusal standing".
 *
 * The heartbeat is the highest-volume background caller in the app, so it is
 * the one that most needs to hear "stop". Before this, `catch` swallowed
 * everything and the next beat went out one interval later regardless — which
 * means a fleet told to back off kept beating on its original cadence, and the
 * shed that refused them bought nothing.
 */
let backpressureUntil = 0;

/**
 * The most of a server's Retry-After the heartbeat will honour — one
 * interval, a hard constraint not caution: the beat renews rows that expire
 * on presenceTtl (300 s), so a five-minute suppression is a user gone
 * invisible with a working phone — the exact failure stay-visible prevents,
 * caused by us. One interval = at most ONE skipped beat, renewal inside the
 * TTL. Honest scope: against our own edge rule it buys little (Cloudflare's
 * 60 s window expires before the next 120 s beat is due); it bites on
 * multi-minute origin windows and on UNSCHEDULED beats — the immediate
 * cold-open/resume beat and manual triggers landing inside a standing
 * refusal, which are exactly the synchronised ones.
 */
export const HEARTBEAT_MAX_BACKOFF_MS = PRESENCE_HEARTBEAT_MS;

/**
 * Stand-down when the server refused but said nothing about for how long.
 *
 * "Refused with no advice" is not permission to beat straight back. An ALB
 * answering 503 with no healthy targets — the shape of the failure that
 * matters most here — sends no `Retry-After` at all.
 */
export const BACKPRESSURE_FLOOR_MS = 5_000;

/** Random span added to any stand-down, so the fleet does not resume together. */
export const BACKPRESSURE_JITTER_MS = 5_000;

/**
 * How long to stand down for, if this error was the server shedding.
 *
 * Keyed on the STATUS, not on the presence of `retryAfterMs`. A 429 or 503
 * with no `Retry-After` header is still the server refusing us, and it is the
 * commonest shape of the one that matters most: an ALB answering 503 with no
 * targets left sends no header at all. Reading only the header would let
 * exactly that case beat straight through the refusal.
 *
 * The jitter and the no-header default are applied HERE, because deciding when
 * to come back is this module's decision. They used to be applied at parse
 * time, which made the stored value uninterpretable — see
 * `backpressureDelayMs`.
 */
function refusalWaitMs(
  err: unknown,
  random: () => number = Math.random,
): number | null {
  const e = err as { status?: unknown; retryAfterMs?: unknown } | null;
  if (e === null || (e.status !== 429 && e.status !== 503)) return null;
  const asked = e.retryAfterMs;
  const floor =
    typeof asked === "number" && Number.isFinite(asked) && asked > 0
      ? asked
      : BACKPRESSURE_FLOOR_MS;
  // Jittered so a fleet told the same thing does not return in the same
  // instant, which is the herd the stand-down exists to prevent.
  //
  // The cap is applied to the FLOOR, before the jitter, not to the total
  // afterwards. Clamping the total maps every ask longer than the cap onto the
  // cap exactly — so a server asking for 300s would put every client in the
  // fleet on the same millisecond, which is the herd again by another route.
  // Capping first leaves the jitter intact on top.
  const capped = Math.min(floor, HEARTBEAT_MAX_BACKOFF_MS - BACKPRESSURE_JITTER_MS);
  return Math.max(0, capped) + random() * BACKPRESSURE_JITTER_MS;
}

/** Send one beat now. Exported so a test can drive it without a real clock. */
export async function beatOnce(
  now: () => number = Date.now,
  // Injectable for the same reason as the clock: a jittered stand-down that
  // cannot be pinned makes any test of its window flaky, and a flaky guard is
  // worse than none.
  random: () => number = Math.random,
): Promise<BeatOutcome> {
  const current = deps;
  if (!current) return "stopped";
  if (!current.isForeground()) return "skipped_background";
  if (inFlight) return "skipped_overlapping";
  // A standing refusal outranks the schedule. Beating into a server that just
  // told us it is shedding is how a refusal wave becomes the next arrival wave.
  if (backpressureUntil > now()) return "skipped_backpressure";

  inFlight = true;
  try {
    const outcome = (await current.ping()) ? "sent" : "skipped_no_fix";
    // A success clears any standing refusal: the server is answering again.
    backpressureUntil = 0;
    return outcome;
  } catch (err) {
    // Still swallowed — a failed beat is not an error the user can act on, and
    // what a RUN of failures means (invisible while being told otherwise) is
    // the stay-visible store's job to surface.
    //
    // But a 429/503 is not an ordinary failure. It is the server saying it is
    // protecting itself, and `ApiError.retryAfterMs` carries how long it asked
    // for. Honouring it here is what makes that value more than decoration.
    const wait = refusalWaitMs(err, random);
    if (wait !== null) {
      backpressureUntil = now() + Math.min(wait, HEARTBEAT_MAX_BACKOFF_MS);
    }
    return "failed";
  } finally {
    inFlight = false;
  }
}

/** Clear any standing refusal. For tests, and for an explicit user action. */
export function clearHeartbeatBackpressure(): void {
  backpressureUntil = 0;
}

/**
 * How much of the FIRST interval may be shaved off, to scatter the fleet. A
 * mass event (push, deploy, task replacement) starts thousands of 120 s
 * timers in the same second and they stay in lockstep FOREVER — 2,100 users
 * re-sending a 4,200-request wave every two minutes; the burst echoes.
 * PHASE, not interval, because the 240 s staleness bound must stay inside
 * presenceTtl (300 s): jittering intervals upward spends that headroom and a
 * long draw pushes a real user past their own TTL. Shortening only the first
 * interval randomises the phase — permanent spread, zero ongoing cost, and
 * the 240 s bound strictly improves.
 */
const HEARTBEAT_PHASE_JITTER = 0.5;

/**
 * A first delay in [0.5, 1.0] x the interval. Never longer. See above.
 *
 * Exported so the NEVER-LONGER property is a test rather than a comment: it is
 * what keeps the 240s staleness bound inside `presenceTtl`, and a later change
 * that widened the jitter symmetrically would look harmless and break it.
 */
export function jitteredFirstDelay(): number {
  const floor = PRESENCE_HEARTBEAT_MS * (1 - HEARTBEAT_PHASE_JITTER);
  return floor + Math.random() * (PRESENCE_HEARTBEAT_MS - floor);
}

/**
 * Self-rescheduling, because the first delay differs from the rest.
 *
 * `setInterval` cannot express "this gap, then that gap", and chaining also
 * means a beat can never be scheduled while the previous one is still being
 * scheduled — `inFlight` already guards overlap, this guards the timer itself.
 */
function schedule(delayMs: number): void {
  timer = setTimeout(() => {
    void beatOnce();
    // Re-arm at the exact interval: the phase is already scattered, and the
    // 240s staleness bound depends on this gap being the interval, not more.
    if (timer !== null) schedule(PRESENCE_HEARTBEAT_MS);
  }, delayMs);
  // Never let a heartbeat hold a node process open. No-op in React Native,
  // where timers have no `unref`.
  (timer as unknown as { unref?: () => void }).unref?.();
}

/**
 * Begin beating. Idempotent: calling it again replaces the dependencies and
 * leaves a single timer running, so the several paths that can make a user
 * visible — join, the visibility toggle, unpausing cues, and re-focusing the
 * home tab while already visible — cannot between them start two.
 *
 * Returns the first beat so a test can await it. Callers fire and forget.
 */
export function startPresenceHeartbeat(
  next: Deps,
  { beatNow = true }: { beatNow?: boolean } = {},
): Promise<BeatOutcome> {
  deps = next;
  if (!timer) {
    schedule(jitteredFirstDelay());
  }
  // `beatNow: false` for callers that JUST wrote presence: "redundant and
  // cheap" is true for one user and false for a fleet — a cold-open wave of
  // 2,100 users publishing then immediately beating is 4,200 duplicate
  // requests inside the arrival burst, and phase jitter cannot reach the
  // first beat. Still the default, because re-focusing after minutes in the
  // background genuinely needs it: visible now vs in up to one interval.
  if (!beatNow) return Promise.resolve("skipped_just_published");
  return beatOnce();
}

/** Stop beating and forget the dependencies. Safe to call when not running. */
export function stopPresenceHeartbeat(): void {
  if (timer) {
    // clearTimeout, not clearInterval: the beat re-arms itself with
    // `setTimeout` so the first gap can be phase-jittered. Nulling `timer` is
    // also what stops the chain re-arming from inside a firing callback.
    clearTimeout(timer);
    timer = null;
  }
  deps = null;
  // A standing refusal must not outlive the heartbeat it applied to: signing
  // out and back in, or toggling visibility, should not inherit a mute.
  backpressureUntil = 0;
}

/** Whether a timer is currently scheduled. For tests and diagnostics. */
export function presenceHeartbeatRunning(): boolean {
  return timer !== null;
}

/**
 * What the screen should show, given the space a beat just resolved. Pure
 * and here, not in session-store.ts, so a plain node test can drive it.
 * Returns null when nothing should be written.
 *
 * Exists because the beat re-resolves the space server-side and used to
 * throw the answer away — Home kept the first-connect space until a tab
 * re-focus (caught on a walk test: visibility followed, the label did not).
 * ONLY ON A REAL CHANGE: this runs on a timer forever and every store write
 * notifies subscribers; comparing ids is what stops a re-render of Home
 * every two minutes. The old lastArrivalVenueId rules are gone WITH the
 * arrival announcement itself — walking is not an event worth interrupting
 * somebody for; what survives is the label following you silently.
 */
export function heartbeatVenuePatch<TVenue extends { id: string }>(
  currentVenueId: string | null,
  resolved: TVenue | null,
): { venue: TVenue | null } | null {
  if (currentVenueId === (resolved?.id ?? null)) return null;
  return { venue: resolved };
}
